import { useEffect, useState } from 'react';

/** Re-renders the caller on an interval so relative times stay live. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const dateOpts = (opts: Intl.DateTimeFormatOptions) => opts;

/** "14 June 2026" / "12–15 June 2026" / "30 May – 2 June 2026" / cross-year. */
export function formatDateRange(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) => d.toLocaleDateString(undefined, opts);
  const sameDay = s.toDateString() === e.toDateString();
  const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
  const sameYear = s.getFullYear() === e.getFullYear();
  if (sameDay) return fmt(s, dateOpts({ day: 'numeric', month: 'long', year: 'numeric' }));
  if (sameMonth) return `${s.getDate()}–${e.getDate()} ${fmt(e, dateOpts({ month: 'long', year: 'numeric' }))}`;
  if (sameYear) {
    return `${fmt(s, dateOpts({ day: 'numeric', month: 'long' }))} – ${fmt(e, dateOpts({ day: 'numeric', month: 'long', year: 'numeric' }))}`;
  }
  return `${fmt(s, dateOpts({ day: 'numeric', month: 'long', year: 'numeric' }))} – ${fmt(e, dateOpts({ day: 'numeric', month: 'long', year: 'numeric' }))}`;
}

/** Compact future delta: "in under a minute" / "in 12 min" / "in 2h 15m" / "in 3 days". */
export function formatRelativeFuture(deltaMs: number): string {
  if (deltaMs <= 0) return 'now';
  const mins = Math.round(deltaMs / 60_000);
  if (mins < 1) return 'in under a minute';
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return remMins ? `in ${hours}h ${remMins}m` : `in ${hours}h`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

/** Shift length as "4h" / "4h 30m" / "45m". */
export function formatDuration(startIso: string, endIso: string): string {
  const mins = Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60_000);
  if (!Number.isFinite(mins) || mins <= 0) return '';
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours === 0) return `${remMins}m`;
  return remMins ? `${hours}h ${remMins}m` : `${hours}h`;
}

export type FestivalState = 'before' | 'during' | 'after';

export function festivalStatus(
  nowMs: number,
  startIso: string,
  endIso: string,
): { state: FestivalState; label: string } {
  const s = Date.parse(startIso);
  const e = Date.parse(endIso);
  if (Number.isFinite(s) && nowMs < s) return { state: 'before', label: `Starts ${formatRelativeFuture(s - nowMs)}` };
  if (Number.isFinite(e) && nowMs > e) return { state: 'after', label: 'Ended' };
  return { state: 'during', label: 'Happening now' };
}

export type ShiftState = 'past' | 'now' | 'next' | 'upcoming';

/** Classify a shift relative to `nowMs`; `isNext` flags the single soonest upcoming shift. */
export function shiftTiming(
  nowMs: number,
  startIso: string,
  endIso: string,
  isNext: boolean,
): { state: ShiftState; label: string | null } {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isFinite(start) && Number.isFinite(end) && nowMs >= start && nowMs <= end) {
    return { state: 'now', label: 'Now' };
  }
  if (Number.isFinite(end) && nowMs > end) return { state: 'past', label: null };
  const rel = formatRelativeFuture(start - nowMs);
  if (isNext) return { state: 'next', label: `Next · ${rel}` };
  return { state: 'upcoming', label: rel };
}
